import React, { useState, useRef, useEffect } from 'react';
import * as S from './styles';
import { Input, Radio, Table, Icon, Loader, Modal, Grid, Button } from 'semantic-ui-react';
import { Field, FormikProps } from 'formik';
import ButtonComponent from '../../../../../../components/Button';
import { CreateClaimEstateInitialInitialValuesType } from '../../Formik/types/CreateClaimEstateInitialValuesTypes';
import { maskCpf, maskCnpj, unmaskCpfOrCnpj } from '../../../../../../services/masks';
import {
  getClaimByTenantDocument,
  getActivePoliciesByTenantDocument
} from '../../../../../../services/claim';
import { ActivePolicy } from '../../../../../../services/bond-insurance-policy';
import moment from 'moment-timezone';
import { getBondInsurancePolicyStatusLabel } from '../../../../../../services/bond-insurance-policy';
import { BondInsuranceHiredDataStatusEnum } from '../../../../../../enums/bond-insurance-hired-data-status.enum';
import { getClaimEstatesStatusText } from '../../../../constants/ClaimEstatesStatusEnum';
import { PushRoute } from '../../../../../../util';
import { useHistory } from 'react-router-dom';
import { ClaimPropertyStatusEnum } from '../../../../../../enums/claim-property-status.enum';

type Policy = {
  id: string;
  createdAt: string;
  insurerName: string;
  estateName: string;
  propertyStreet: string;
  propertyNumber: string;
  propertyDistrict: string;
  propertyCity: string;
  propertyState: string;
  propertyZipCode: string;
  status: string;
  originalPolicy: ActivePolicy;
};

const InitialVerifications = ({
  values,
  setFieldValue,
  onNext
}: FormikProps<CreateClaimEstateInitialInitialValuesType> & { onNext: (policy?: any) => void }) => {
  const history = useHistory();
  const tenantType = values.tenantType;
  const documentLabel = tenantType === 'pf' ? 'CPF' : tenantType === 'pj' ? 'CNPJ' : '';
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<ActivePolicy | null>(null);
  const [claimInProgress, setClaimInProgress] = useState<any | null>(null);
  const [openClaimModal, setOpenClaimModal] = useState(false);
  const [occupationType, setOccupationType] = useState<'occupied' | 'unoccupied' | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const occupiedDocuments = [
    'Contrato de locação',
    'Contração de administração',
    'Contrato Locatário/Garantti (somente para abertura de sinistros da Garantti)',
    'Declaração de débitos – devidamente assinada pela imobiliária, detalhando todos os valores em aberto',
    'Boletos de aluguel em aberto',
    'Procuração do escritório parceiro da seguradora, conforme modelos para download',
    'Guia de IPTU – em caso de débito(s) em aberto',
    'Boletos originais dos condomínios com descritivo – em caso de débito(s) em aberto',
    'Boletos de água – em caso de débito(s) em aberto',
    'Boletos de luz – em caso de débito(s) em aberto',
    'Contato telefônico e e-mail do inquilino para cobrança.'
  ];

  const unoccupiedDocuments = [
    'Contrato de locação',
    'Contração de administração',
    'Contrato Locatário/Garantti (somente para abertura de sinistros da Garantti)',
    'Declaração de débitos – devidamente assinada pela imobiliária, detalhando todos os valores em aberto',
    'Boletos de aluguel em aberto',
    'Procuração do escritório parceiro da seguradora, conforme modelos para download',
    'Guia de IPTU – em caso de débito(s) em aberto',
    'Boletos originais dos condomínios com descritivo – em caso de débito(s) em aberto',
    'Boletos de água – em caso de débito(s) em aberto',
    'Boletos de luz – em caso de débito(s) em aberto'
  ];

  const unoccupiedAdditionalDocuments = [
    'Em caso de entrega de chaves, além dos documentos acima (exceto PROCURAÇÃO), enviar também:',
    'Planilha de débitos finais – devidamente assinada pela administradora, detalhando todos os valores em aberto incluindo valores dos orçamentos;',
    'Termo de entrega de chaves – obrigatório que o documento seja assinado pela imobiliária e pelo locatário. Em caso de ausência da assinatura do locatário, enviar o documento assinado por duas testemunhas qualificadas com nome e CPF e a cópia da identidade delas;',
    'Vistoria inicial – obrigatório que o documento seja assinado pela imobiliária e pelo locatário do contrato (se solicitada indenização de danos do imóvel e/ou de pintura (tendo sido contratada a cobertura);',
    'Declaração de débitos – devidamente assinada pela imobiliária, detalhando todos os valores em aberto;',
    'Vistoria final – obrigatório que o documento seja assinado pela imobiliária e pelo locatário. Em caso de ausência da assinatura do locatário, enviar o documento assinado por duas testemunhas qualificadas com nome e CPF e a cópia da identidade delas (se solicitada indenização de danos do imóvel e/ou de pintura (tendo sido contratada a cobertura);',
    'Orçamentos – conforme detalhamento abaixo:'
  ];

  const handleTenantTypeChange = (type: 'pf' | 'pj') => {
    setFieldValue('tenantType', type);
    setFieldValue('claimTenant.document', '');
    setPolicies([]);
    setSelectedPolicyId(null);
    setClaimInProgress(null);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  const searchPoliciesAndClaims = async (document: string) => {
    if (!tenantType || !document) {
      setPolicies([]);
      setClaimInProgress(null);
      return;
    }

    const cleanDocument = unmaskCpfOrCnpj(document);
    const requiredLength = tenantType === 'pf' ? 11 : 14;

    if (cleanDocument.length < requiredLength) {
      setPolicies([]);
      setClaimInProgress(null);
      return;
    }

    setLoadingPolicies(true);
    try {
      // Busca apólices e sinistros em paralelo
      const [policiesResponse, claimResponse] = await Promise.all([
        getActivePoliciesByTenantDocument(document),
        getClaimByTenantDocument(document)
      ]);

      if (policiesResponse) {
        const mappedPolicies: Policy[] = policiesResponse.map((p: ActivePolicy) => ({
          id: p.id,
          status: p.status,
          createdAt: p.createdAt || "",
          insurerName: p.insurer.name,
          estateName: p.bondInsurance.estate.name,
          propertyStreet: p.bondInsurance.property?.street,
          propertyNumber: p.bondInsurance.property?.number,
          propertyDistrict: p.bondInsurance.property?.district,
          propertyCity: p.bondInsurance.property?.city,
          propertyState: p.bondInsurance.property?.state,
          propertyZipCode: p.bondInsurance.property?.zip_code,
          originalPolicy: p
        }));
        setPolicies(mappedPolicies);
      } else {
        setPolicies([]);
      }

      if (claimResponse?.data) {
        setClaimInProgress(claimResponse.data);
      } else {
        setClaimInProgress(null);
      }
    } catch (error) {
      console.error('Erro ao buscar apólices/sinistros:', error);
      setPolicies([]);
      setClaimInProgress(null);
    } finally {
      setLoadingPolicies(false);
    }
  };

  const handleDocumentChange = (_e: any, { value }: { value: string }) => {
    if (!tenantType) return;
    const maskedValue = tenantType === 'pf' ? maskCpf(value) : maskCnpj(value);
    setFieldValue('claimTenant.document', maskedValue);

    // Limpa o timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Busca automática com debounce de 500ms quando atingir o tamanho correto
    const cleanDocument = unmaskCpfOrCnpj(maskedValue);
    const requiredLength = tenantType === 'pf' ? 11 : 14;

    if (cleanDocument.length >= requiredLength) {
      searchTimeoutRef.current = setTimeout(() => {
        searchPoliciesAndClaims(maskedValue);
      }, 500);
    } else {
      setPolicies([]);
      setClaimInProgress(null);
    }
  };

  // Limpa o timeout quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Preenche o occupationType ao retomar um rascunho ou clonar
  useEffect(() => {
    if (values.claimProperty?.status) {
      if (values.claimProperty.status === ClaimPropertyStatusEnum.occupied) {
        setOccupationType('occupied');
      } else if (values.claimProperty.status === ClaimPropertyStatusEnum.vacant) {
        setOccupationType('unoccupied');
      }
    }
  }, [values.claimProperty?.status]);

  // Sincroniza o occupationType com o claimProperty.status no Formik
  useEffect(() => {
    if (occupationType === 'occupied') {
      setFieldValue('claimProperty.status', ClaimPropertyStatusEnum.occupied);
    } else if (occupationType === 'unoccupied') {
      setFieldValue('claimProperty.status', ClaimPropertyStatusEnum.vacant);
    }
  }, [occupationType, setFieldValue]);

  const handlePolicySelect = (policyId: string) => {
    setSelectedPolicyId(policyId);
    const policy = policies.find(p => p.id === policyId);
    setSelectedPolicy(policy?.originalPolicy || null);
  };

  const formatAddress = (policy: Policy) => {
    const parts = [
      policy.propertyStreet,
      policy.propertyNumber,
      policy.propertyDistrict,
      policy.propertyCity,
      policy.propertyState,
      policy.propertyZipCode
    ].filter(Boolean);
    return parts.join(', ');
  };

  const formatDate = (date: string) => {
    if (!date) return '00/00/0000';
    return moment(date).format('DD/MM/YYYY');
  };

  return (
    <S.InitialVerificationsContainer>
      <S.HeaderSection>
        <S.IntroText>
          <p> Para que a abertura de Sinistro seja registrada como <S.RedText>válida dentro da data limite</S.RedText>, é necessário realizar o <b>upload da documentação</b> dentro do período válido.</p>
          <p> Caso não tenha acesso a toda a documentação, você pode seguir com a criação do processo de Sinistro no formato de <b>Rascunho</b> e realizar o upload da documentação em outro momento.</p>
        </S.IntroText>
      </S.HeaderSection>

      <S.Section>
        <S.TenantTypeRow>
          <S.TenantTypeColumn>
            <S.Label>
              Tipo de locatário <S.Required>*</S.Required>
            </S.Label>
            <S.RadioGroup>
              <Radio
                label="Pessoa Física"
                name="tenantType"
                value="pf"
                checked={tenantType === 'pf'}
                onChange={() => handleTenantTypeChange('pf')}
              />
              <Radio
                label="Pessoa Jurídica"
                name="tenantType"
                value="pj"
                checked={tenantType === 'pj'}
                onChange={() => handleTenantTypeChange('pj')}
              />
            </S.RadioGroup>
          </S.TenantTypeColumn>
          {tenantType && (
            <S.InputGroup>
              <S.Label>
                {documentLabel} <S.Required>*</S.Required>
              </S.Label>
              <Field
                name="claimTenant.document"
                component={Input}
                value={values.claimTenant.document || ''}
                onChange={handleDocumentChange}
              />
            </S.InputGroup>
          )}
        </S.TenantTypeRow>
      </S.Section>

      {tenantType && policies.length > 0 && (
        <S.Section>
          <S.SectionTitle>Apólices ativas neste {documentLabel}</S.SectionTitle>
          <S.SectionDescription>
            Selecione a apólice de referência para preencher as informações automaticamente.
          </S.SectionDescription>
          <S.TableWrapper>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell></Table.HeaderCell>
                  <Table.HeaderCell>Criado em</Table.HeaderCell>
                  <Table.HeaderCell>Seguradora</Table.HeaderCell>
                  <Table.HeaderCell>Imobiliária</Table.HeaderCell>
                  <Table.HeaderCell>Endereço</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {policies.map((policy) => (
                  <Table.Row key={policy.id}>
                    <Table.Cell>
                      <Radio
                        name="policy"
                        checked={selectedPolicyId === policy.id}
                        onChange={() => handlePolicySelect(policy.id)}
                      />
                    </Table.Cell>
                    <Table.Cell>{formatDate(policy.createdAt)}</Table.Cell>
                    <Table.Cell>{policy.insurerName || '-'}</Table.Cell>
                    <Table.Cell>{policy.estateName || '-'}</Table.Cell>
                    <Table.Cell>{formatAddress(policy)}</Table.Cell>
                    <Table.Cell>{policy.status ? getBondInsurancePolicyStatusLabel(policy.status as BondInsuranceHiredDataStatusEnum) : '-'}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </S.TableWrapper>
        </S.Section>
      )}
      {loadingPolicies && (
        <S.Section>
          <Loader active inline="centered">
            Buscando apólices...
          </Loader>
        </S.Section>
      )}

      {claimInProgress && (
        <S.Section>
          <S.SectionTitle>Este locatário já possui um Sinistro em andamento</S.SectionTitle>
          <S.SectionDescription>
            Verifique os detalhes e finalize o processo já existente antes de iniciar um novo Sinistro.
          </S.SectionDescription>
          <S.ExistingClaimCard>
            <S.ClaimDetails>
              <S.ClaimDetailItem>
                <S.ClaimLabel>Endereço</S.ClaimLabel>
                <S.ClaimValue>
                  {claimInProgress.claimProperty?.street && (
                    <>
                      {claimInProgress.claimProperty.street}
                      {claimInProgress.claimProperty.number && `, ${claimInProgress.claimProperty.number}`}
                      {claimInProgress.claimProperty.district && `, ${claimInProgress.claimProperty.district}`}
                      {claimInProgress.claimProperty.city && `, ${claimInProgress.claimProperty.city}`}
                      {claimInProgress.claimProperty.state && `-${claimInProgress.claimProperty.state}`}
                    </>
                  )}
                </S.ClaimValue>
              </S.ClaimDetailItem>

              <S.ClaimDetailsRow>
                <S.ClaimDetailItem>
                  <S.ClaimLabel>Data de solicitação</S.ClaimLabel>
                  <S.ClaimValue>
                    {claimInProgress.createdAt
                      ? moment(claimInProgress.createdAt).subtract(3, 'h').format('DD/MM/YYYY HH:mm')
                      : '-'}
                  </S.ClaimValue>
                </S.ClaimDetailItem>
                <S.ClaimDetailItem>
                  <S.ClaimLabel>Inquilino</S.ClaimLabel>
                  <S.ClaimValue>{claimInProgress.claimTenant?.fullName || '-'}</S.ClaimValue>
                </S.ClaimDetailItem>
                <S.ClaimDetailItem>
                  <S.ClaimLabel>Seguradora</S.ClaimLabel>
                  <S.ClaimValue>{claimInProgress.insurer?.name || '-'}</S.ClaimValue>
                </S.ClaimDetailItem>
                <S.ClaimDetailItem>
                  <S.ClaimLabel>Imobiliária</S.ClaimLabel>
                  <S.ClaimValue>{claimInProgress.estate?.name || '-'}</S.ClaimValue>
                </S.ClaimDetailItem>
                <S.ClaimDetailItem>
                  <S.ClaimLabel>Status</S.ClaimLabel>
                  <S.ClaimValue>
                    {claimInProgress.status
                      ? getClaimEstatesStatusText(claimInProgress.status)
                      : '-'}
                  </S.ClaimValue>
                </S.ClaimDetailItem>
              </S.ClaimDetailsRow>
            </S.ClaimDetails>
            <S.ArrowButton onClick={() => {
              PushRoute(history, {}, 'claim_estates', {
                claimId: claimInProgress.id
              });
            }}>
              <Icon name="arrow right" size="large" />
            </S.ArrowButton>
          </S.ExistingClaimCard>
        </S.Section>
      )}

      <S.SectionWithBorder>
        <S.OccupationSectionContent>
          <S.LabelWithIcon>
            Selecione o tipo de ocupação do imóvel<S.Required>*</S.Required>
            <Icon name="info circle" />
          </S.LabelWithIcon>
          <S.OccupationButtons>
            <S.OccupationButton
              selected={occupationType === 'occupied'}
              onClick={() => setOccupationType('occupied')}
            >
              <Icon name="home" />
              <span>Imóvel ocupado</span>
            </S.OccupationButton>
            <S.OccupationButton
              selected={occupationType === 'unoccupied'}
              onClick={() => setOccupationType('unoccupied')}
            >
              <Icon name="home" />
              <span>Imóvel Desocupado</span>
            </S.OccupationButton>
          </S.OccupationButtons>

          <S.DocumentsInfoSection>
            <S.LabelWithIcon>
              Quais documentos devo incluir?
              <Icon name="info circle" />
            </S.LabelWithIcon>
            <S.DocumentsDescription>
              A depender da ocupação do imóvel a lista de documentos pode ser diferente. Clique nos botões abaixo para conferir a documentação necessária para cada tipo de ocupação.
            </S.DocumentsDescription>

            {occupationType && (
              <S.DocumentsListContainer>
                <S.DocumentsListTitle>Lista de documentos</S.DocumentsListTitle>
                <S.DocumentsList>
                  {occupationType === 'occupied' ? (
                    occupiedDocuments.map((doc, index) => (
                      <S.DocumentItem key={index}>
                        {doc}
                        {index < occupiedDocuments.length - 1 && ';'}
                      </S.DocumentItem>
                    ))
                  ) : (
                    unoccupiedDocuments.map((doc, index) => (
                      <S.DocumentItem key={index}>
                        {doc}
                        {index < unoccupiedDocuments.length - 1 && ';'}
                      </S.DocumentItem>
                    ))
                  )}
                </S.DocumentsList>

                {occupationType === 'unoccupied' && (
                  <>
                    <S.DocumentsListAdditional>
                      {unoccupiedAdditionalDocuments.map((doc, index) => (
                        <S.DocumentItem key={index}>
                          {doc}
                        </S.DocumentItem>
                      ))}
                    </S.DocumentsListAdditional>

                    <S.BudgetsContainer>
                      <S.BudgetCard>
                        <S.BudgetCardTitle>Sinistros – Pottencial Seguradora</S.BudgetCardTitle>
                        <S.BudgetCardContent>
                          <strong>Danos ao imóvel</strong> <br />• 1 orçamento, contendo nome/CPF dos prestadores, valor detalhado de cada serviço e de cada material. <br /><br /> <strong>Pintura</strong> <br />• 1 orçamento, contendo nome/CPF dos prestadores, cômodos que serão pintados, metragem, valores de material e de mão de obra.
                        </S.BudgetCardContent>
                      </S.BudgetCard>

                      <S.BudgetCard>
                        <S.BudgetCardTitle>Sinistros – Demais Seguradoras</S.BudgetCardTitle>
                        <S.BudgetCardContent>
                          <strong>Danos ao imóvel</strong> <br />• 2 orçamentos, contendo nome/CPF dos prestadores, valor detalhado de cada serviço e de cada material. <br /> <br /><strong>Pintura</strong> <br />• 2 orçamentos, contendo nome/CPF dos prestadores, cômodos que serão pintados, metragem da área, valores de material e de mão de obra.
                        </S.BudgetCardContent>
                      </S.BudgetCard>
                    </S.BudgetsContainer>
                  </>
                )}
              </S.DocumentsListContainer>
            )}
          </S.DocumentsInfoSection>
        </S.OccupationSectionContent>
      </S.SectionWithBorder>

      {/* Documentação do sinistro */}
      <S.InfoBanner>
        <span>
          Caso não tenha acesso a toda a documentação, você pode seguir com a <b>criação do processo de Sinistro no formato de Rascunho</b> e realizar o upload da documentação em outro momento
        </span>
      </S.InfoBanner>

      <S.FooterButtons>
        <ButtonComponent color="red">
          <Icon name="times" />
          Cancelar
        </ButtonComponent>
        <ButtonComponent color="green" onClick={() => onNext(selectedPolicy)}>
          <Icon name="check" />
          Próximo
        </ButtonComponent>
      </S.FooterButtons>

      <Modal
        onClose={() => setOpenClaimModal(false)}
        onOpen={() => { }}
        open={openClaimModal}
        size={'large'}
        style={{ zIndex: 11 }}
      >
        <Modal.Header>Este locatário já possui um Sinistro em andamento</Modal.Header>
        <Modal.Content scrolling>
          <div style={{ fontSize: 16 }}>
            Verifique os detalhes e finalize o processo já existente antes de iniciar um novo Sinistro.
          </div>

          {claimInProgress && (
            <div
              style={{
                flex: 1,
                padding: 10,
                border: '1px solid #ccc',
                borderWidth: 0.5,
                borderRadius: 6,
                marginTop: 20,
                marginBottom: 5
              }}
            >
              <div style={{ display: 'flex', flex: 1, gap: 20 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    gap: 20
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 16, fontWeight: 'bold' }}>Endereço</span>
                    <span style={{ flex: 1, fontSize: 16 }}>
                      {claimInProgress.claimProperty?.street && (
                        <>
                          {claimInProgress.claimProperty.street},{' '}
                          {claimInProgress.claimProperty.number},{' '}
                          {claimInProgress.claimProperty.district},{' '}
                          {claimInProgress.claimProperty.city}
                          {'-'}
                          {claimInProgress.claimProperty.state}
                        </>
                      )}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flex: 1,
                      justifyContent: 'space-between',
                      gap: 20
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 16, fontWeight: 'bold' }}>
                        Data de solicitação
                      </span>
                      <span style={{ flex: 1, fontSize: 16 }}>
                        {claimInProgress.createdAt
                          ? moment(claimInProgress.createdAt)
                            .subtract(3, 'h')
                            .format('DD/MM/YYYY HH:mm:ss')
                          : '-'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 16, fontWeight: 'bold' }}>Inquilino</span>
                      <span style={{ flex: 1, fontSize: 16 }}>
                        {claimInProgress.claimTenant?.fullName || '-'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 16, fontWeight: 'bold' }}>Seguradora</span>
                      <span style={{ flex: 1, fontSize: 16 }}>
                        {claimInProgress.insurer?.name || '-'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 16, fontWeight: 'bold' }}>Imobiliária</span>
                      <span style={{ flex: 1, fontSize: 16 }}>
                        {claimInProgress.estate?.name || '-'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 16, fontWeight: 'bold' }}>Status</span>
                      <span style={{ flex: 1, fontSize: 16 }}>
                        {claimInProgress.status
                          ? getClaimEstatesStatusText(claimInProgress.status)
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    type="button"
                    color="blue"
                    onClick={() => {
                      setOpenClaimModal(false);
                      PushRoute(history, {}, 'claim_estates', {
                        claimId: claimInProgress.id
                      });
                    }}
                  >
                    <Icon name="arrow right" /> Acessar Sinistro
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Grid>
            <Grid.Column width={16}>
              <Button
                onClick={() => setOpenClaimModal(false)}
                type="submit"
                color="grey"
              >
                Fechar
              </Button>
            </Grid.Column>
          </Grid>
        </Modal.Actions>
      </Modal>
    </S.InitialVerificationsContainer>
  );
};

export default InitialVerifications;